import { useMemo } from "react";
import type { Booking } from "@scheduler/shared";

interface TimelineBarProps {
  bookings: Booking[];
  currentTime: Date;
}

interface Segment {
  start: number;
  end: number;
  booked: boolean;
  booking?: Booking;
}

/**
 * Horizontal timeline bar showing the working day (8am–6pm).
 * Booked segments show meeting titles at low contrast — readable up close,
 * invisible from across the room.
 */
export function TimelineBar({ bookings, currentTime }: TimelineBarProps) {
  const DAY_START_HOUR = 8;
  const DAY_END_HOUR = 18;
  const TOTAL_MINUTES = (DAY_END_HOUR - DAY_START_HOUR) * 60;

  const segments = useMemo(() => {
    const sorted = [...bookings].sort(
      (a, b) =>
        new Date(a.start_time).getTime() - new Date(b.start_time).getTime(),
    );

    const dayStart = new Date(currentTime);
    dayStart.setHours(DAY_START_HOUR, 0, 0, 0);

    const result: Segment[] = [];
    let cursor = 0;

    for (const booking of sorted) {
      const bStart = new Date(booking.start_time);
      const bEnd = new Date(booking.end_time);

      const startMin = Math.max(
        0,
        (bStart.getTime() - dayStart.getTime()) / 60_000,
      );
      const endMin = Math.min(
        TOTAL_MINUTES,
        (bEnd.getTime() - dayStart.getTime()) / 60_000,
      );

      if (endMin <= 0 || startMin >= TOTAL_MINUTES) continue;

      if (startMin > cursor) {
        result.push({ start: cursor, end: startMin, booked: false });
      }

      result.push({
        start: Math.max(cursor, startMin),
        end: endMin,
        booked: true,
        booking,
      });
      cursor = endMin;
    }

    if (cursor < TOTAL_MINUTES) {
      result.push({ start: cursor, end: TOTAL_MINUTES, booked: false });
    }

    return result;
  }, [bookings, currentTime]);

  const nowMarker = useMemo(() => {
    const dayStart = new Date(currentTime);
    dayStart.setHours(DAY_START_HOUR, 0, 0, 0);
    const elapsed = (currentTime.getTime() - dayStart.getTime()) / 60_000;
    const pct = (elapsed / TOTAL_MINUTES) * 100;
    if (pct < 0 || pct > 100) return null;
    return pct;
  }, [currentTime]);

  // Hour labels — every hour
  const hours = [];
  for (let h = DAY_START_HOUR; h <= DAY_END_HOUR; h++) {
    const pct =
      ((h - DAY_START_HOUR) / (DAY_END_HOUR - DAY_START_HOUR)) * 100;
    const isEven = h % 2 === 0;
    hours.push({ pct, hour: h, major: isEven });
  }

  function formatHour(h: number): string {
    const suffix = h >= 12 ? "pm" : "am";
    const n = h === 0 || h === 12 ? 12 : h > 12 ? h - 12 : h;
    return `${n}${suffix}`;
  }

  return (
    <div className="w-full">
      {/* Hour tick marks */}
      <div className="relative h-2">
        {hours.map((h) => (
          <div
            key={h.hour}
            className={`absolute bottom-0 w-px ${h.major ? "h-2 bg-white/15" : "h-1 bg-white/8"}`}
            style={{ left: `${h.pct}%` }}
          />
        ))}
      </div>

      {/* Main bar */}
      <div className="relative h-3">
        {/* Bar background */}
        <div className="absolute inset-0 rounded-full bg-white/[0.06] overflow-hidden">
          {segments.map((seg, i) => {
            if (!seg.booked) return null;
            const left = (seg.start / TOTAL_MINUTES) * 100;
            const width = ((seg.end - seg.start) / TOTAL_MINUTES) * 100;

            return (
              <div
                key={i}
                className="absolute top-0 bottom-0 rounded-full"
                style={{
                  left: `${left}%`,
                  width: `${width}%`,
                  background: "rgba(255, 255, 255, 0.3)",
                }}
              />
            );
          })}
        </div>

        {/* Now marker — extends above and below the bar */}
        {nowMarker !== null && (
          <>
            <div
              className="absolute w-[2px] bg-white z-10"
              style={{ left: `${nowMarker}%`, top: "-6px", bottom: "-6px" }}
            />
            <div
              className="absolute w-[6px] h-[6px] rounded-full bg-white z-10 -translate-x-1/2"
              style={{ left: `${nowMarker}%`, top: "-8px" }}
            />
          </>
        )}
      </div>

      {/* Hour labels */}
      <div className="relative h-5 mt-0.5">
        {hours.map((h) => (
          <span
            key={h.hour}
            className="absolute text-[10px] text-white/30 tabular-nums -translate-x-1/2"
            style={{ left: `${h.pct}%` }}
          >
            {formatHour(h.hour)}
          </span>
        ))}
      </div>
    </div>
  );
}
